import React, { useState, useEffect } from 'react';
import axios from 'axios';
import "./styles.css";
import { 
  Typography, 
  Grid, 
  Box, 
  Button, 
  Paper, 
  Divider, 
  Stepper, 
  Step, 
  StepLabel,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PhotoIcon from "@mui/icons-material/Photo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { jobStatusList } from "./Accordianlist";
import Details from './Details';
import ProfileTab from './ProfileTab';
import SubHeader from '../../components/Subheader/Subheader';

const ServiceRequests = () => {
  const [executed, setExecuted] = useState(3);
  const [expanded, setExpanded] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobDetails, setJobDetails] = useState({});
  const [homeownerDetails, setHomeownerDetails] = useState({});
  const [jobQuotes, setJobQuotes] = useState({});
  const [quoteSummaries, setQuoteSummaries] = useState({});
  const [activeTab, setActiveTab] = useState("timeline");
  const [plumberImages, setPlumberImages] = useState({});
  const [homeownerImages, setHomeownerImages] = useState({});
  const [loadingQuotes, setLoadingQuotes] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [loadingInvoice, setLoadingInvoice] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Function to show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle closing notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError("No token found! Please log in.");
      setLoading(false);
      return;
    }

    const fetchJobs = async () => {
      try {
        const response = await axios.get('https://jobmanagement-497524552274.us-central1.run.app/api/jobs/list', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Jobs fetched:', response.data);
        setJobs(response.data);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setError('Failed to fetch jobs. Please try refreshing the page.');
      }
      setLoading(false);
    };

    fetchJobs();
  }, []);

  const fetchHomeownerDetails = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get('https://usermanagementsystem-497524552274.us-central1.run.app/adminuser/get-profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Homeowner details fetched:', response.data);
      setHomeownerDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch homeowner details:', error);
      showNotification('Failed to load homeowner details', 'error');
    }
  };

  const fetchUnpaidQuotes = async (jobId) => {
    const token = localStorage.getItem('token');
    setLoadingQuotes(prev => ({ ...prev, [jobId]: true }));
    
    try {
      console.log(`Fetching unpaid quotes for job ${jobId}`);
      const response = await axios.get('https://quotemanagement-497524552274.us-central1.run.app/quotes/unpaid', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('All unpaid quotes:', response.data);
      
      // Filter quotes for this specific job
      const allJobQuotes = response.data.filter(quote => quote.jobId === jobId);
      console.log(`Filtered quotes for job ${jobId}:`, allJobQuotes);
      
      // Limit to only 2 quotes
      const limitedJobQuotes = allJobQuotes.slice(0, 2);
      
      if (allJobQuotes.length === 0) {
        console.log(`No quotes found for job ${jobId}`);
      } else {
        console.log(`Displaying ${limitedJobQuotes.length} out of ${allJobQuotes.length} quotes for job ${jobId}`);
      }
      
      setJobQuotes(prev => ({ ...prev, [jobId]: limitedJobQuotes }));
    } catch (error) {
      console.error(`Failed to fetch unpaid quotes for job ${jobId}:`, error);
      showNotification('Failed to load quotes information', 'error');
      setJobQuotes(prev => ({ ...prev, [jobId]: [] }));
    } finally {
      setLoadingQuotes(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const fetchQuoteSummary = async (jobId) => {
    const token = localStorage.getItem('token');
    try {
      console.log(`Fetching quote summary for job ${jobId}`);
      const response = await axios.get(`https://quotemanagement-497524552274.us-central1.run.app/quotes/${jobId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`Quote summary for job ${jobId}:`, response.data);
      setQuoteSummaries(prev => ({ ...prev, [jobId]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch quote summary for job ${jobId}:`, error);
      
      // Create a fallback/default summary object when API fails
      const fallbackSummary = {
        subtotal: 0,
        tax: 0,
        labor: 0,
        discount: 0,
        totalAmount: 0,
        error: true
      };
      
      setQuoteSummaries(prev => ({ ...prev, [jobId]: fallbackSummary }));
      
      // Only show notification if it's not a 404 (which could be expected for new jobs)
      if (error.response && error.response.status !== 404) {
        showNotification('Failed to load quote summary', 'error');
      }
    }
  };

  const fetchPlumberImages = async (jobId) => {
    const token = localStorage.getItem('token');
    setLoadingImages(prev => ({ ...prev, [jobId]: true }));
    
    try {
      console.log(`Fetching plumber images for job ${jobId}`);
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/plumber-image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Check if we received a valid image
      if (response.data.size > 0) {
        // Convert blob to URL
        const imageUrl = URL.createObjectURL(response.data);
        setPlumberImages(prev => ({ ...prev, [jobId]: imageUrl }));
        console.log(`Plumber image loaded for job ${jobId}`);
      } else {
        console.log(`No plumber image for job ${jobId}`);
        setPlumberImages(prev => ({ ...prev, [jobId]: null }));
      }
    } catch (error) {
      console.error(`Failed to fetch plumber images for job ${jobId}:`, error);
      setPlumberImages(prev => ({ ...prev, [jobId]: null }));
    } finally {
      setLoadingImages(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const fetchHomeownerImages = async (jobId) => {
    const token = localStorage.getItem('token');
    try {
      console.log(`Fetching homeowner images for job ${jobId}`);
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Check if we received a valid image
      if (response.data.size > 0) {
        // Convert blob to URL
        const imageUrl = URL.createObjectURL(response.data);
        setHomeownerImages(prev => ({ ...prev, [jobId]: imageUrl }));
        console.log(`Homeowner image loaded for job ${jobId}`);
      } else {
        console.log(`No homeowner image for job ${jobId}`);
        setHomeownerImages(prev => ({ ...prev, [jobId]: null }));
      }
    } catch (error) {
      console.error(`Failed to fetch homeowner images for job ${jobId}:`, error);
      setHomeownerImages(prev => ({ ...prev, [jobId]: null }));
    }
  };

  const handleChange = (panel, item) => async (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    setActiveTab("timeline");
    
    if (isExpanded) {
      const token = localStorage.getItem('token');
      
      // Fetch all necessary data when expanding an accordion
      try {
        console.log(`Fetching details for job ${item.id}`);
        // Job details
        const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log(`Job details for ${item.id}:`, response.data);
        setJobDetails(prevDetails => ({ ...prevDetails, [item.id]: response.data }));
        
        // Fetch all other details
        await fetchHomeownerDetails();
        await fetchUnpaidQuotes(item.id);
        await fetchQuoteSummary(item.id);
        await fetchPlumberImages(item.id);
        await fetchHomeownerImages(item.id);
      } catch (error) {
        console.error(`Failed to fetch job details for job ${item.id}:`, error);
        showNotification('Failed to load job details', 'error');
        setError('Failed to fetch job details');
      }
    }
  };

  const handleSetActive = (status) => {
    if (status) setExecuted(4);
  };

  const downloadInvoice = async (jobId) => {
    const token = localStorage.getItem('token');
    setLoadingInvoice(prev => ({ ...prev, [jobId]: true }));
    
    try {
      console.log(`Downloading invoice for job ${jobId}`);
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/invoices/view/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Check if we received a valid PDF
      if (response.data.size === 0) {
        console.error('Received empty PDF');
        showNotification('The invoice file is empty or not available', 'error');
        return;
      }
      
      // Create a download link with PDF content type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${jobId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
      
      showNotification('Invoice downloaded successfully', 'success');
    } catch (error) {
      console.error(`Failed to download invoice for job ${jobId}:`, error);
      let errorMessage = 'Failed to download invoice';
      
      if (error.response) {
        console.error('Error details:', error.response.data);
        console.error('Status code:', error.response.status);
        
        if (error.response.status === 404) {
          errorMessage = 'Invoice not found. It may not have been generated yet.';
        } else if (error.response.status === 403) {
          errorMessage = 'You do not have permission to download this invoice.';
        }
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setLoadingInvoice(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const markJobAsSettled = async (jobId) => {
    const token = localStorage.getItem('token');
    try {
      console.log(`Marking job ${jobId} as settled`);
      await axios.put(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/settle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update job status in the UI
      setJobs(jobs.map(job => 
        job.id === jobId ? { ...job, status: 'SETTLED' } : job
      ));
      
      // Update job details
      if (jobDetails[jobId]) {
        setJobDetails({
          ...jobDetails,
          [jobId]: { ...jobDetails[jobId], status: 'SETTLED' }
        });
      }
      
      showNotification('Job marked as settled successfully', 'success');
    } catch (error) {
      console.error(`Failed to mark job ${jobId} as settled:`, error);
      showNotification('Failed to mark job as settled', 'error');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress />
      <Typography sx={{ ml: 2 }}>Loading service requests...</Typography>
    </Box>
  );
  
  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column' }}>
      <Typography color="error" gutterBottom>{error}</Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>Refresh Page</Button>
    </Box>
  );

  // Status steps for timeline
  const getStatusSteps = (status) => {
    const allSteps = ['CREATED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'SETTLED'];
    const currentStep = allSteps.indexOf(status);
    return { steps: allSteps, activeStep: currentStep !== -1 ? currentStep : 0 };
  };

  return (
    <div className="JobList-accordionPg" 
         style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', minHeight: '100vh', padding: '20px' }}>
      <SubHeader name="Service Requests" options={jobStatusList} />
      
      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      {jobs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>No service requests found.</Typography>
        </Box>
      ) : (
        <Grid container justifyContent="center" spacing={2} style={{ width: '100%' }}>
          {jobs.map((item, index) => (
            <Grid item xs={12} sm={10} md={8} key={index}>
              <Accordion
                onChange={handleChange(index, item)}
                expanded={expanded === index}
                sx={{ backgroundColor: "#EAEAEA", boxShadow: "none", borderRadius: "8px", width: '100%', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#0D5C75", fontSize: "32px" }} />}>
                  <Grid pl={1}>
                    <Typography sx={{ fontSize: "15px", fontWeight: "600", color: "#1F1F1F" }}>
                      Job ID: {item.id}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", color: "#1F1F1F" }}>
                      Created On: {item.scheduledTime}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "500", color: "#1F1F1F" }}>
                      Status: <span style={{ 
                        color: item.status === 'SETTLED' ? 'green' : 
                               item.status === 'COMPLETED' ? 'blue' : 
                               item.status === 'IN_PROGRESS' ? 'orange' : 'black'
                      }}>{item.status}</span>
                    </Typography>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '16px', backgroundColor: '#ffffff' }}>
                  {jobDetails[item.id] ? (
                    <Box sx={{ width: '100%' }}>
                      {/* Navigation tabs */}
                      <Box sx={{ display: 'flex', mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Button 
                          onClick={() => setActiveTab("timeline")} 
                          sx={{ 
                            fontWeight: activeTab === "timeline" ? 'bold' : 'normal',
                            borderBottom: activeTab === "timeline" ? 2 : 0,
                            borderColor: activeTab === "timeline" ? 'primary.main' : 'transparent'
                          }}
                        >
                          Timeline
                        </Button>
                        <Button 
                          onClick={() => setActiveTab("homeowner")} 
                          sx={{ 
                            fontWeight: activeTab === "homeowner" ? 'bold' : 'normal',
                            borderBottom: activeTab === "homeowner" ? 2 : 0,
                            borderColor: activeTab === "homeowner" ? 'primary.main' : 'transparent'
                          }}
                        >
                          Homeowner Details
                        </Button>
                        <Button 
                          onClick={() => setActiveTab("details")} 
                          sx={{ 
                            fontWeight: activeTab === "details" ? 'bold' : 'normal',
                            borderBottom: activeTab === "details" ? 2 : 0,
                            borderColor: activeTab === "details" ? 'primary.main' : 'transparent'
                          }}
                        >
                          Job Details
                        </Button>
                        <Button 
                          onClick={() => setActiveTab("quotes")} 
                          sx={{ 
                            fontWeight: activeTab === "quotes" ? 'bold' : 'normal',
                            borderBottom: activeTab === "quotes" ? 2 : 0,
                            borderColor: activeTab === "quotes" ? 'primary.main' : 'transparent'
                          }}
                        >
                          Quotes
                        </Button>
                        <Button 
                          onClick={() => setActiveTab("images")} 
                          sx={{ 
                            fontWeight: activeTab === "images" ? 'bold' : 'normal',
                            borderBottom: activeTab === "images" ? 2 : 0,
                            borderColor: activeTab === "images" ? 'primary.main' : 'transparent'
                          }}
                        >
                          Images
                        </Button>
                      </Box>

                      {/* Timeline View */}
                      {activeTab === "timeline" && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="h6" gutterBottom>Job Status Timeline</Typography>
                          <Paper elevation={1} sx={{ p: 3 }}>
                            {jobDetails[item.id] && (
                              <Box sx={{ width: '100%' }}>
                                <Stepper activeStep={getStatusSteps(jobDetails[item.id].status).activeStep} alternativeLabel>
                                  {getStatusSteps(jobDetails[item.id].status).steps.map((label) => (
                                    <Step key={label}>
                                      <StepLabel>{label}</StepLabel>
                                    </Step>
                                  ))}
                                </Stepper>
                                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                  {jobDetails[item.id].status !== 'SETTLED' && jobDetails[item.id].status === 'COMPLETED' && (
                                    <Button 
                                      variant="contained" 
                                      color="primary" 
                                      startIcon={<CheckCircleIcon />}
                                      onClick={() => markJobAsSettled(item.id)}
                                    >
                                      Mark as Settled
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      )}

                      {/* Homeowner Details */}
                      {activeTab === "homeowner" && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="h6" gutterBottom>Homeowner Details</Typography>
                          <Paper elevation={1} sx={{ p: 3 }}>
                            {homeownerDetails ? (
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" fontWeight="bold">Name</Typography>
                                  <Typography>{homeownerDetails.firstName} {homeownerDetails.lastName}</Typography>
                                  
                                  <Typography variant="subtitle1" fontWeight="bold" mt={2}>Email</Typography>
                                  <Typography>{homeownerDetails.email}</Typography>
                                  
                                  <Typography variant="subtitle1" fontWeight="bold" mt={2}>Phone</Typography>
                                  <Typography>{homeownerDetails.phone}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" fontWeight="bold">Address</Typography>
                                  <Typography>
                                    {homeownerDetails.address?.street}<br />
                                    {homeownerDetails.address?.city}, {homeownerDetails.address?.state} {homeownerDetails.address?.zip}
                                  </Typography>
                                  
                                  <Typography variant="subtitle1" fontWeight="bold" mt={2}>Scheduled Time</Typography>
                                  <Typography>{jobDetails[item.id]?.scheduledTime}</Typography>
                                </Grid>
                              </Grid>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress size={24} sx={{ mr: 1 }} />
                                <Typography>Loading homeowner details...</Typography>
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      )}

                      {/* Job Details */}
                      {activeTab === "details" && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="h6" gutterBottom>Job Details</Typography>
                          <Paper elevation={1} sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" fontWeight="bold">Job ID</Typography>
                                <Typography>{jobDetails[item.id].id}</Typography>
                                
                                <Typography variant="subtitle1" fontWeight="bold" mt={2}>Status</Typography>
                                <Typography>{jobDetails[item.id].status}</Typography>
                                
                                <Typography variant="subtitle1" fontWeight="bold" mt={2}>Created Date</Typography>
                                <Typography>{jobDetails[item.id].createdDate}</Typography>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle1" fontWeight="bold">Scheduled Time</Typography>
                                <Typography>{jobDetails[item.id].scheduledTime}</Typography>
                                
                                <Typography variant="subtitle1" fontWeight="bold" mt={2}>Description</Typography>
                                <Typography>{jobDetails[item.id].description}</Typography>
                                
                                <Typography variant="subtitle1" fontWeight="bold" mt={2}>Service Type</Typography>
                                <Typography>{jobDetails[item.id].serviceType}</Typography>
                              </Grid>
                            </Grid>
                            
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                              <Button 
                                variant="contained" 
                                color="primary"
                                startIcon={loadingInvoice[item.id] ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                onClick={() => downloadInvoice(item.id)}
                                disabled={loadingInvoice[item.id]}
                              >
                                {loadingInvoice[item.id] ? 'Downloading...' : 'Download Invoice'}
                              </Button>
                              
                              {jobDetails[item.id].status !== 'SETTLED' && jobDetails[item.id].status === 'COMPLETED' && (
                                <Button 
                                  variant="contained" 
                                  color="success" 
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => markJobAsSettled(item.id)}
                                >
                                  Mark as Settled
                                </Button>
                              )}
                            </Box>
                          </Paper>
                        </Box>
                      )}

                      {/* Quotes Tab */}
                      {activeTab === "quotes" && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="h6" gutterBottom>Quotes</Typography>
                          
                          {/* Items Needed */}
                          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" fontWeight="bold">Quote Items (Displaying up to 2 quotes)</Typography>
                              {jobQuotes[item.id] && jobQuotes[item.id].length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  Showing {jobQuotes[item.id].length} {jobQuotes[item.id].length === 1 ? 'quote' : 'quotes'}
                                </Typography>
                              )}
                            </Box>
                            
                            {loadingQuotes[item.id] ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} sx={{ mr: 1 }} />
                                <Typography>Loading quote items...</Typography>
                              </Box>
                            ) : jobQuotes[item.id] && jobQuotes[item.id].length > 0 ? (
                              <TableContainer>
                                <Table>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Quote #</TableCell>
                                      <TableCell>Item</TableCell>
                                      <TableCell>Price</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {jobQuotes[item.id].map((quote, idx) => (
                                      quote.items && quote.items.map((quoteItem, itemIdx) => (
                                        <TableRow key={`${idx}-${itemIdx}`}>
                                          <TableCell>{idx + 1}</TableCell>
                                          <TableCell>{quoteItem.name}</TableCell>
                                          <TableCell>R{quoteItem.price.toFixed(2)}</TableCell>
                                        </TableRow>
                                      ))
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography color="textSecondary">No quotes found for this job</Typography>
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  sx={{ mt: 2 }}
                                  onClick={() => fetchUnpaidQuotes(item.id)}
                                >
                                  Refresh Quotes
                                </Button>
                              </Box>
                            )}
                          </Paper>
                          
                          {/* Quote Summary */}
                          <Paper elevation={1} sx={{ p: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Quote Summary</Typography>
                            
                            {quoteSummaries[item.id] ? (
                              <Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <List>
                                      <ListItem>
                                        <ListItemText 
                                          primary="Product Cost" 
                                          secondary={`R${(quoteSummaries[item.id].productCost || 0).toFixed(2)}`} 
                                        />
                                      </ListItem>
                                      <ListItem>
                                        <ListItemText 
                                          primary="Labor Cost" 
                                          secondary={`R${(quoteSummaries[item.id].laborCost || 0).toFixed(2)}`} 
                                        />
                                      </ListItem>
                                    </List>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <List>
                                      <ListItem>
                                        <ListItemText 
                                          primary="Quote ID" 
                                          secondary={quoteSummaries[item.id].quoteId || 'N/A'} 
                                        />
                                      </ListItem>
                                      <ListItem>
                                        <ListItemText 
                                          primary="Total Cost" 
                                          secondary={`R${(quoteSummaries[item.id].totalCost || 0).toFixed(2)}`}
                                          primaryTypographyProps={{ fontWeight: 'bold' }}
                                          secondaryTypographyProps={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                                        />
                                      </ListItem>
                                    </List>
                                  </Grid>
                                </Grid>
                            
                              </Box>
                            ) : quoteSummaries[item.id]?.error ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography color="error">Failed to load quote summary</Typography>
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  sx={{ mt: 2 }}
                                  onClick={() => fetchQuoteSummary(item.id)}
                                >
                                  Retry
                                </Button>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} sx={{ mr: 1 }} />
                                <Typography>Loading quote summary...</Typography>
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      )}

                      {/* Images Tab */}
                      {activeTab === "images" && (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <Typography variant="h6" gutterBottom>Job Images</Typography>
                          
                          <Grid container spacing={3}>
                            {/* Homeowner Images */}
                            <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                  <PhotoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                  Homeowner Provided Images
                                </Typography>
                                
                                {homeownerImages[item.id] ? (
                                  <Card>
                                    <CardMedia
                                      component="img"
                                      height="300"
                                      image={homeownerImages[item.id]}
                                      alt="Homeowner provided image"
                                      sx={{ objectFit: 'contain' }}
                                    />
                                    <CardContent>
                                      <Typography variant="body2" color="text.secondary">
                                        Image provided by homeowner showing the issue
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                ) : (
                                  <Box sx={{ 
                                    height: 300, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1
                                  }}>
                                    <Typography color="text.secondary">No homeowner images available</Typography>
                                  </Box>
                                )}
                              </Paper>
                            </Grid>
                            
                            {/* Plumber Images */}
                            <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                  <PhotoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                  Plumber Provided Images
                                </Typography>
                                
                                {loadingImages[item.id] ? (
                                  <Box sx={{ 
                                    height: 300, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1
                                  }}>
                                    <CircularProgress size={40} />
                                  </Box>
                                ) : plumberImages[item.id] ? (
                                  <Card>
                                    <CardMedia
                                      component="img"
                                      height="300"
                                      image={plumberImages[item.id]}
                                      alt="Plumber provided image"
                                      sx={{ objectFit: 'contain' }}
                                    />
                                    <CardContent>
                                      <Typography variant="body2" color="text.secondary">
                                        Image provided by plumber showing the repair
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                ) : (
                                  <Box sx={{ 
                                    height: 300, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1
                                  }}>
                                    <Typography color="text.secondary">No plumber images available</Typography>
                                  </Box>
                                )}
                              </Paper>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ ml: 2 }}>Loading job details...</Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default ServiceRequests;